import React from 'react';
import Link, {LinkProps} from '@mui/material/Link';
import {NavLink} from 'react-router';
import {useDispatch} from 'react-redux';

import {AttentionItem} from '../../../types/models/Attention';
import {linkPath as cuttingLinkPath} from '../../cutting/CuttingDetail/CuttingLink';
import {linkPath as timberStorageLinkPath} from '../../timberStorage/TimberStorageDetail/TimberStorageLink';
import {linkPath as shipmentLinkPath} from '../../shipment/ShipmentDetail/ShipmentLink';

interface LinkRouterProps extends LinkProps {
    to: string;
}

function LinkRouter(props: LinkRouterProps) {
    return <Link {...props} component={NavLink as any}/>;
}

/**
 * The path builder of each kind of record the panel can point at.
 *
 * The web app has no flat page for a shipment: it lives under the storage it
 * is in, and the storage under the cutting. The paths are built by the very
 * helpers the rest of the application uses for its links, so a link in the
 * panel lands on the same page a list row would.
 */
type PathBuilder = (dispatch: any, id: string) => Promise<string | null | undefined>;

const pathBuilders: Record<AttentionItem['entity'], PathBuilder> = {
    'Cutting': cuttingLinkPath,
    'TimberStorage': timberStorageLinkPath,
    'Shipment': shipmentLinkPath,
};

type ProviderAttentionLinkProps = {
    item: AttentionItem,
    children: React.ReactNode,
};

/**
 * The name of a record that needs attention, linked to the record itself.
 *
 * The path of a record is made of its parents, so it is only known once the
 * record has been read. Until then the name is shown plain, because a link
 * that leads nowhere is worse than no link at all.
 */
const ProviderAttentionLink = ({item, children}: ProviderAttentionLinkProps) => {

    const dispatch = useDispatch();
    const [path, setPath] = React.useState('');

    React.useEffect(() => {
        let stale = false;

        const builder = pathBuilders[item.entity];
        if (!builder) {
            return;
        }

        builder(dispatch, item.entityId)
            .then(resolved => {
                if (stale) return;
                // A record whose parents cannot be read has no path to go to.
                if (!resolved || !resolved.startsWith('/')) return;
                setPath(`/dashboard${resolved}`);
            })
            .catch(() => {
                // The record could not be read; the name is shown without a link.
            });

        return () => {
            stale = true;
        };
    }, [dispatch, item.entity, item.entityId]);

    if (!path) {
        return <>{children}</>;
    }

    return (
        <LinkRouter to={path} underline="hover" viewTransition={true}>
            {children}
        </LinkRouter>
    );
};

export default ProviderAttentionLink;
